<template>
  <div>
    <div
      class="chart-wrapper w-full flex relative"
      :class="{ 'is-negative' : getChartIsNegative, 'is-positive' : !getChartIsNegative }"
    >    
      <!-- Chart Y Labels Start -->
      <div class="chart-axis-y-wrapper min-w-10 h-full flex flex-col">
        <h2 class="chart-axis-y-caption ts--accent--2">
          <span class="text-gray02">
            {{ columnSelected.chartProjection.title }}
          </span>
          <span
            v-if="columnSelected.chartProjection.subtitle"
            class="text-gray04"
          >
            ・ {{ columnSelected.chartProjection.subtitle }}
          </span>
        </h2>
        <div
          v-for="(item, index) in getYLabels"
          :key="`chart-item-label-${index}`"
          class="chart-axis-y flex-1 block w-full px-2 text-gray05 flex justify-end items-start relative"
        >
          <div class="flex justify-end text-gray02">
            {{ item.formatted }}
          </div>
        </div>
      </div>
      <!-- Chart Y Axis Labels End -->

      <!-- Chart Content Start -->
      <div class="chart-content w-full flex flex-col relative">
        <!-- Chart Content X Lines Start -->
        <div class="chart-content-x-lines h-full flex flex-col">
          <div
            v-for="(item, index) in getYLabels"
            :key="`chart-item-y-lines-${index}`"
            class="chart-content-x-line flex-1 block w-full px-2 text-gray05 flex justify-end items-start"
          />
        </div>
        <!-- Chart Content X Lines End -->

        <!-- Chart Content Bars Start -->
        <div class="chart-content-bars w-full h-full flex border-l border-b border-gray05 px-4 z-10">
          <div
            v-for="(item, index) in getChartData"
            :key="`chart-item=-${index}-${getItemKey}`"
            class="chart-content-bars-wrapper h-full flex-1 block flex relative text-gray02 items-end"
            :style="{ marginLeft: getMargin, marginRight: getMargin }"
            :class="{ 'is-label' : getXLabels.indexOf(index) > -1 }"
            :data-year="item.year"
          >
            <div
              v-tooltip="getTooltip(index)"
              class="chart-content-bars-item w-full h-full block"
              :style="item.styles"
            />
            <div class="chart-content-bars-caption w-full block h-5 absolute bottom-0 -mb-6 flex justify-center">
              {{ item.year }}
            </div>
          </div>        
        </div>
        <!-- Chart Content Bars End -->      
      </div>
      <!-- Chart Content End -->
    </div>
    <div
      v-if="assumption"
      id="active-installation-period"
      :style="{ paddingLeft: horizontalDistanceBetweenStarBarAndPeriodBar}"
    >
      <div
        class="bar"
        :style="{ width: horizontalDistanceBetweenStartBarAndEndBar }"
      />
      <span
        v-tooltip="activeInstallationPeriodTooltip"
        class="ts--accent--3 text-gray03"
      >Active Installation Period</span>
    </div>
  </div>
</template>

<script>
import { randomString, removeNegative } from '@/util/Functions'
import getChartLabelsWithNegative from '@/util/getChartLabelsWithNegative'
import formatStudyData from '@/formatters/formatStudyData'
import ImpactAssumptions from '@/models/ImpactAssumptions'

export default {
  name: 'ChartProjection',
  props: {
    assumption: {
      type: Object,
      default() {
        return new ImpactAssumptions()
      }
    },
    columnSelected: {
      type: Object,
      required: true
    },
    columns: {
      type: [Array, Object],
      required: true
    },
    projectionsByYear: {
      type: Array,
      required: true
    },
  },
  data: () => ({
    horizontalDistanceBetweenStartBarAndEndBar : 0,
    horizontalDistanceBetweenStarBarAndPeriodBar: 0
  }),
  computed: {    
    assumptionModel() {
      return new ImpactAssumptions(this.assumption)
    },
    getItemKey() {
      return randomString(10)
    },
    getYLabels() {      
      const labels = getChartLabelsWithNegative(this.getMax, this.getMin)
      return labels.map((label) => {
        return {
          value: label,
          formatted: formatStudyData(this.columnSelected.key, label)
        }
      })
    },
    getXLabels() {
      return this.getProjectionData.map((item, index) => index).filter((item, index, arr) => {
        if(index === 0 || (index % 10 === 0 && ((arr.length - index) >= 9)) || index == arr.length-1) return true
        return false
      })
    },
    getMargin() {
      return `${(100 / this.getProjectionData.length) / 8}%`
    },
    getMin() {
      return this.getProjectionData.map((a) => a).sort((a, b) => a.current - b.current)[0].current
    },
    getMax() {
      return this.getProjectionData.map((a) => a).sort((a, b) => a.current - b.current)[this.getProjectionData.length-1].current
    },
    getUnformattedMax() {
      return this.getProjectionData.map((a) => a).sort((a, b) => removeNegative(a.current) - removeNegative(b.current))[this.getProjectionData.length-1].current
    },
    getUnformattedMin() {
      const filtered = this.getProjectionData.filter((item) => this.getChartIsNegative && item.current < 0 || !this.getChartIsNegative && item.current > 0).sort((a, b) => removeNegative(a.current) - removeNegative(b.current))
      if(filtered.length) return filtered[0].current
      return 0
    },

    getChartIsNegative() {
      return this.getUnformattedMax < 0 ? true : false
    },
    getChartData() {
      let labels = getChartLabelsWithNegative(this.getMax, this.getMin)      
      if (this.getChartIsNegative) labels.reverse()
      
      const lowestLabel = labels[0]
      const highestLabel = labels[labels.length - 1]
      const range = Math.abs(lowestLabel) + Math.abs(highestLabel)

      const chartData = this.getProjectionData.map((item) => {
        
        const pct = Math.abs(item.current) * 100 / range
        let height
        if (!range) {
          height = 0
        } else {
          height = pct === 0 ? '.5%' : `${pct}%`
        }
        const isItemNegative = item.current < 0 ? true : false
        const colorByGraph = item.current < 0 ? '#D65C5A' : `#4B8CA6`
        const backgroundColor = pct === 0 ? 'gray' : colorByGraph
        return { 
          ...item, 
          isNegative: isItemNegative,
          styles: {
            backgroundColor,
            height,
            position: 'relative',
            bottom: `${(100 / (labels.length - 1)) * labels.indexOf(0)}%`,
            transform: isItemNegative ? `rotateX(180deg)` : ''
          }
        }
      })

      return chartData
    },
    getProjectionData() {
      return this.projectionsByYear.map((projection) => {
        return {
          current: projection[this.columnSelected.key].current,
          cummulative: projection[this.columnSelected.key].cummulative,
          year: projection.year
        }
      })
    },
    activeInstallationPeriodTooltip() {
      return {
        html: true,
        content: `<strong>Active Installation Period</strong> <br /> This is when additional units are installing the required measure(s)`,
        placement: 'right',
        offset: 10
      }
    }
  },
  watch: {
    columnSelected() {
      setTimeout(() => {
        this.drawTheActiveInslattionPeriod()
      }, 500)
    }
  },
  mounted() {
    this.drawTheActiveInslattionPeriod()
  },
  methods: {

    drawTheActiveInslattionPeriod() {
      if (!this.assumption) return

      const activeInstallationPeriod = this.assumptionModel.getActiveInstallationPeriod({ format: 'YYYY' })
      const activeIinstallationPeriodEl = document.getElementById('active-installation-period')      
      
      const activeInstallationPeriodStartBarEl = document.querySelector(`[data-year='${activeInstallationPeriod[0]}']`)
      const activeInstallationPeriodEndBarEl = document.querySelector(`[data-year='${activeInstallationPeriod[1]}']`)

      this.horizontalDistanceBetweenStarBarAndPeriodBar = `${activeInstallationPeriodStartBarEl.getBoundingClientRect().left - activeIinstallationPeriodEl.getBoundingClientRect().left}px` 
      this.horizontalDistanceBetweenStartBarAndEndBar = `${activeInstallationPeriodEndBarEl.getBoundingClientRect().left - activeInstallationPeriodStartBarEl.getBoundingClientRect().left}px`          

    },

    getTooltip(index) {
 
      const item = this.projectionsByYear[index]

      const html = true
      
      // let content = '<table>'
      // content += `
      //     <thead>
      //       <th>${item.year}</th>
      //       <th></th>
      //       ${ debug ? '<th>CUMULATIVE</th>' : ''}
      //     </thead>
      //     <tbody>
      // `
      // this.columns.forEach((column) => {
      //   content += `
      //     <tr class="${column.key === this.columnSelected.key ? 'active' : ''}">
      //       <td class="key"> 
      //         ${column.chartProjection.title} 
      //         <span>${column.chartProjection.subtitle ? ' (' + column.chartProjection.subtitle + ')' : ''}</span>              
      //       </td>
      //       <td> ${formatForecastProjection({ value: item[column.key].current, columnSelectedKey:  column.key })} </td>
      //       ${ debug ? `<td> ${formatForecastProjection({ value: item[column.key].cummulative, columnSelectedKey:  column.key })} </td>` : ''}
      //     </tr>
      //   `
      // })
      // this.columns.forEach((column) => {
        // content += `
        //   <tr>
        //     <td class="key"> 
        //       ${this.columnSelected.chartProjection.title} 
        //       <span>${this.columnSelected.chartProjection.subtitle ? ' (' + this.columnSelected.chartProjection.subtitle + ')' : ''}</span>              
        //     </td>
        //     <td> ${formatForecastProjection({ value: item[this.columnSelected.key].current, columnSelectedKey:  this.columnSelected.key })} </td>
        //     ${ debug ? `<td> ${formatForecastProjection({ value: item[this.columnSelected.key].cummulative, columnSelectedKey:  this.columnSelected.key })} </td>` : ''}
        //   </tr>
        // `
      // })

      // content += `</tbody></table>`
      let content = `${ formatStudyData(this.columnSelected.key, item[this.columnSelected.key].current) }`
      return {
        html,
        content,
        // trigger: 'manual',
        // show: true,
        // classes: 'tooltip-projections-chart' 
      }
    },

    onyNumbers(value) {
      return parseFloat(value.toString().replace('-', ''))
    }
  }
}
</script>

<style lang="scss" scoped>

.chart-wrapper {
  margin-top: 35px;
  height: 400px;
}

.chart-content-bars-wrapper {  
   &:after {
    content: "";
    width: 2px;
    height: 7px;
    background-color: #D7D2CB;
    position: absolute;
    bottom: -7px;
    margin-left: calc(50% - 2px);
  }

  &.is-label {
    + div .chart-content-bars-caption::before {
      content: "";
      background-color: white;
      position: absolute;
      left: -200%;
      width: 400%;
      bottom: 0;
      top: 13%;
      z-index: -1;
    }
    .chart-content-bars-caption {
      opacity: 1;
    }
  }

  &:hover {
    .chart-content-bars-caption {
      opacity: 1;
      z-index: 3;
    }
    + .chart-content-bars-wrapper .chart-content-bars-caption {
      opacity: 0;
    }
  }
}

.chart-content-bars-item {
  transition: height 0.45s cubic-bezier(.17,.67,.83,.67), background-color 0.45s cubic-bezier(.17,.67,.83,.67);
  transform-origin: bottom;
  height: 0;  
}
.chart-content-bars-caption {
  opacity: 0;
  transition: 300ms ease-in-out all;
}

.chart-axis-y-wrapper {
  position: relative;
  transition: 300ms ease-in-out all;
  padding-left: 30px;  
  // border: 1px solid red;
  &:after {
    content: "";
    width: 1px;
    right: -1px;
    background-color: #D7D2CB;
    position: absolute;
  }
  &:after {
    height: 30px;
    top: -30px;
  }
  // &:before {
  //   height: 10px;
  //   bottom: -10px;
  // }
  .chart-axis-y {
    min-width: 62px;
    > div {
      position: relative;
      top: -20px
    }    
  }
}

.chart-axis-y-caption {
  position: absolute;
  left: 0;
  transform: rotate(270deg) translateX(-50%);
  transform-origin: left top;
  top: 50%;
  width: auto;
  white-space: nowrap;
}

.chart-content-x-lines {
  position: absolute;
  width: 100%;
  .chart-content-x-line {
    opacity: 0.4;
    border-top: 1px solid #CDCCCC;
    width: 100%;
    margin-top: -1px;
    &:first-of-type {
      display: none;
    }
  }
}



.is-positive {

  .chart-axis-y-wrapper {
    flex-direction: column-reverse;
    .chart-axis-y {
      &:first-of-type {
        height: 0;
        flex: 0;
      }
    }
  }
}


.is-negative {

  .chart-axis-y-wrapper {
    
    .chart-axis-y {
      &:last-of-type {
        height: 0;
        flex: 0;
      }
    }
  }
}

#active-installation-period {
  margin-top: 40px;
  transition: 300ms ease-in-out all;  
  .bar {
    border-top: 1px solid #CDCCCC;
    position: relative;
    transition: inherit;
    &:before, &:after {
      content: "";
      position: absolute;
      background-color: #CDCCCC;
      height: 9px;
      width: 1px;
      transform: translateY(-50%);
    }
    &:after {
      right: 0;
    }
  }
}
</style>

<style lang="scss">
.tooltip-projections-chart {
  background: #FFFFFF;
  border: 2px solid #E4EEF2;
  box-sizing: border-box;
  box-shadow: 0px 24px 48px rgba(0, 42, 58, 0.1);
  border-radius: 10px;
  padding: 12px;

  .tooltip-inner {
    max-width: initial;
    padding: 0;
  }

  table {
    thead {
      border-bottom: 1px solid #E4EEF2;      
    }
    th {
      font-weight: bold;
      line-height: 16px;
      letter-spacing: 3px;
      text-transform: uppercase;
      font-size: 16px;
      color: #151C22;
      padding-bottom: 10px;
      padding-left: 15px;
      text-align: left;
    }

    tr {
      td {
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 120%;
        color: #383B44;
        padding-top: 12px;
        padding-left: 15px;
        // padding-right: 24px;
        &.key {
          font-weight: bold;
          font-size: 14px;
          color: #151C22;
        }
      }
      &:first-of-type {
        td {
          padding-top: 22px;
        }
      }
      &.active td {
        color: red;
      }
      
    }
  }
}
</style>